
import { Step } from "@/types/recipe";
import { useState } from "react";
import { Check } from "lucide-react";

interface RecipeStepsProps {
  steps: (Step | string)[];
  temperatureUnit: "C" | "F";
}

const RecipeSteps = ({ steps, temperatureUnit }: RecipeStepsProps) => {
  const [completedSteps, setCompletedSteps] = useState<number[]>([]);
  const [activeStep, setActiveStep] = useState<number | null>(null);

  const convertTemperature = (temp: string, originalUnit: "C" | "F") => {
    const tempValue = parseInt(temp);
    
    // If the original unit matches desired unit, return as is
    if (originalUnit === temperatureUnit) {
      return temp;
    }
    
    // Convert F to C
    if (originalUnit === "F" && temperatureUnit === "C") {
      return Math.round((tempValue - 32) * 5 / 9).toString();
    }
    
    // Convert C to F
    if (originalUnit === "C" && temperatureUnit === "F") {
      return Math.round((tempValue * 9 / 5) + 32).toString();
    }
    
    return temp;
  };

  const convertTemperatureInText = (text: string) => {
    return text.replace(/(\d+)°([CF])/g, (match, temp, unit) => {
      const convertedTemp = convertTemperature(temp, unit as "C" | "F");
      return `${convertedTemp}°${temperatureUnit}`;
    });
  };

  const formatTime = (timeString: string | undefined) => {
    if (!timeString) return '';
    const match = timeString.match(/(\d+)\s*minutes?/);
    if (!match) return timeString;
    
    const minutes = parseInt(match[1]);
    if (minutes >= 60) {
      const hours = Math.floor(minutes / 60);
      const remainingMinutes = minutes % 60;
      if (remainingMinutes === 0) {
        return `${hours} ${hours === 1 ? 'hour' : 'hours'}`;
      }
      return `${hours} ${hours === 1 ? 'hour' : 'hours'} ${remainingMinutes} minutes`;
    }
    return `${minutes} minutes`;
  };

  const toggleStep = (index: number) => {
    setCompletedSteps(prev => 
      prev.includes(index) 
        ? prev.filter(i => i !== index)
        : [...prev, index]
    );
  };

  const renderStep = (step: Step | string, index: number) => {
    const isCompleted = completedSteps.includes(index);
    const isActive = activeStep === index;

    if (typeof step === 'string') {
      const convertedInstruction = convertTemperatureInText(step);
      return (
        <div 
          key={index} 
          className={`flex space-x-4 group cursor-pointer rounded-lg p-3 transition-all border-2
            ${isActive ? 'bg-orange-50/50 border-orange-200' : 'hover:bg-gray-50/50 border-transparent'}`}
          onClick={() => toggleStep(index)}
          onMouseEnter={() => setActiveStep(index)}
          onMouseLeave={() => setActiveStep(null)}
        >
          <div className={`flex-shrink-0 w-8 h-8 flex items-center justify-center rounded-full text-primary font-medium transition-all
            ${isCompleted ? 'bg-primary text-white' : 'bg-primary/10'}`}
          >
            {isCompleted ? <Check className="w-4 h-4" /> : index + 1}
          </div>
          <div className="flex-1 space-y-1">
            <p className={`text-gray-700 ${isCompleted ? 'line-through text-gray-400' : ''}`}>
              {convertedInstruction}
            </p>
          </div>
        </div>
      );
    }

    const convertedInstruction = convertTemperatureInText(step.instruction);
    return (
      <div 
        key={index} 
        className={`flex space-x-4 group cursor-pointer rounded-lg p-3 transition-all border-2
          ${isActive ? 'bg-orange-50/50 border-orange-200' : 'hover:bg-gray-50/50 border-transparent'}`}
        onClick={() => toggleStep(index)}
        onMouseEnter={() => setActiveStep(index)}
        onMouseLeave={() => setActiveStep(null)}
      >
        <div className={`flex-shrink-0 w-8 h-8 flex items-center justify-center rounded-full text-primary font-medium transition-all
          ${isCompleted ? 'bg-primary text-white' : 'bg-primary/10'}`}
        >
          {isCompleted ? <Check className="w-4 h-4" /> : index + 1}
        </div>
        <div className="flex-1 space-y-1">
          <p className={`text-gray-700 ${isCompleted ? 'line-through text-gray-400' : ''}`}>
            {convertedInstruction}
          </p>
          {(step.time || step.temperature) && (
            <p className="text-sm">
              {step.temperature && (
                <span className="font-semibold text-gray-900">
                  {convertTemperature(step.temperature, "C")}°{temperatureUnit}
                </span>
              )}
              {step.time && step.temperature && (
                <span className="text-gray-400 mx-2">•</span>
              )}
              {step.time && (
                <span className="font-semibold text-gray-900">
                  {formatTime(step.time)}
                </span>
              )}
            </p>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-4">
      {steps.map((step, index) => renderStep(step, index))}
    </div>
  );
};

export default RecipeSteps;
